import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { BarChart3 } from 'lucide-react';

interface AnalyticsData {
  totalLicenses: number;
  activeLicenses: number;
  totalRevenue: number;
  activeUsers: number;
  licensesByType: Record<string, number>;
  revenueByMonth: Record<string, number>;
}

function Analytics() {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/analytics`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Insights"
        icon={BarChart3}
        title={t('analytics.title')}
        subtitle={t('analytics.subtitle')}
      />

      {analytics ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('analytics.totalLicenses')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.totalLicenses}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('analytics.activeLicenses')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.activeLicenses}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('analytics.totalRevenue')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${analytics.totalRevenue.toFixed(2)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('analytics.activeUsers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.activeUsers}</div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('analytics.noData')}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.licensesByType')}</CardTitle>
          <CardDescription>{t('analytics.distributionDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {analytics && Object.keys(analytics.licensesByType).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(analytics.licensesByType).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="font-medium">{type}</span>
                  <span className="text-muted-foreground">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground">{t('dashboard.noData')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Analytics;
